import { X, ExternalLink, ShieldCheck, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";

import { ExtractionStageChip } from "./ExtractionStageChip";
import { VerificationStatusBadges } from "./VerificationStatusBadges";
import {
  buildBusinessRulesHref,
  buildFinalCheckerHref,
  buildVerifyHref,
} from "../../utils/verifyHref";
import type { DocumentRow } from "../../../types/documents";

interface DocumentDetailPanelProps {
  doc: DocumentRow;
  onClose: () => void;
  /**
   * Backend tracking table the doc lives in. Threaded through into the
   * Verification / Business Rules / Final Checker URLs so the cockpit's
   * `verification_home` call queries the right table. Defaults to
   * `doc_extraction_tracking` when omitted (matches the OCR Tracking
   * page); Template Engine passes `config_studio_tracking`.
   */
  tableName?: string;
}

function formatTime(iso: string | null | undefined, fallback = "—"): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString();
}

/**
 * Colors that mean "the verification step has produced a real result"
 * — pass (green/emerald), warning (yellow/amber/orange), failure
 * (red/rose), or any other definitive signal palette the backend uses
 * (blue/cyan). Mirrors VerificationStatusBadges.COLOR but deliberately
 * excludes "white": the reference treats white as the placeholder state
 * for "slot exists but pipeline hasn't reached it yet" (see ocr-ui
 * chips-cell.jsx — white chips are explicitly non-clickable), and the
 * Final Checker / Verification gating should respect that.
 */
const SETTLED_SIGNALS = new Set([
  "green",
  "emerald",
  "yellow",
  "amber",
  "orange",
  "red",
  "rose",
  "blue",
  "cyan",
]);

/**
 * A verification-map entry is "settled" once it carries one of the
 * known result colors. Empty strings, `"white"`, missing keys, and any
 * other value all count as unsettled — the button gating below will
 * disable / hide CTAs for those.
 */
function isSettledSignal(value: unknown): boolean {
  return (
    typeof value === "string" &&
    SETTLED_SIGNALS.has(value.trim().toLowerCase())
  );
}

/**
 * Is TV fully passed? The Business Rules CTA is gated on this — only a
 * green TV means extraction + verification produced clean output that BR
 * can build on top of. Yellow/red/white all mean TV isn't ready yet
 * (warnings, failures, or pipeline still working), so the BR button
 * stays hidden until the user finishes TV and the backend stamps green.
 *
 * We avoid keying off `status` because the string shape varies across
 * pipeline versions, while `verification_status.TV` is the stable
 * contract the badge component already relies on.
 */
function isTvCompleted(doc: DocumentRow): boolean {
  const tv = doc.verification?.TV;
  return typeof tv === "string" && tv.toLowerCase() === "green";
}

/**
 * Can the user enter the Tools View (verification cockpit) yet? If both
 * TV and BC verification slots are still "white" (no settled color), the
 * pipeline hasn't produced anything to verify — opening the cockpit
 * would show empty panes. Gate the CTA so users don't end up on a dead
 * page.
 */
function canOpenVerification(doc: DocumentRow): boolean {
  return (
    isSettledSignal(doc.verification?.TV) ||
    isSettledSignal(doc.verification?.BC)
  );
}

/**
 * Should we surface the Final Checker (Business Rule verification) CTA?
 *
 * The reference (ocr-ui/src/shared/table/chips-cell.jsx) lets the user
 * click the BR chip whenever it has a non-white color — i.e. the
 * pipeline has reached the "ready for BR" state and stamped a settled
 * signal on the BR slot. We mirror that: surface the button as soon as
 * `verification_status.BR` carries any color, including yellow/red
 * (which mean BR ran but needs human review — exactly the case where
 * Final Checker is most useful).
 */
function isFinalCheckerAvailable(doc: DocumentRow): boolean {
  return isSettledSignal(doc.verification?.BR);
}

/**
 * Right-rail panel showing one document's metadata. Designed to slot into
 * the same flex column the ProcessesPage uses for its right details rail.
 */
export function DocumentDetailPanel({
  doc,
  onClose,
  tableName,
}: DocumentDetailPanelProps) {
  const hrefOpts = tableName ? { tableName } : undefined;
  return (
    <div className="flex h-full flex-col">
      <header className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 flex-shrink-0">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-gray-900">
            {doc.filename}
          </h2>
          <p className="font-mono text-xs text-gray-500 truncate">{doc.process_id}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <ExtractionStageChip status={doc.status} />
          {/* `item` intentionally omitted — badges in the detail panel are
              read-only signals. Navigation lives in the footer CTAs. */}
          <VerificationStatusBadges status={doc.verification} />
        </div>

        <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Field label="Customer" value={doc.customer_name || "—"} />
          <Field
            label="Workflow"
            value={<span className="font-mono text-xs">{doc.workflow_id || "—"}</span>}
          />
          <Field
            label="Layout"
            value={
              <span className="font-mono text-xs break-all" title={doc.layout_id}>
                {doc.layout_id || "—"}
              </span>
            }
          />
          <Field label="Service" value={doc.service || "—"} />
          <Field label="Uploader" value={doc.uploader || "—"} />
          <Field label="Started" value={formatTime(doc.startedAtIso, doc.start_time || "—")} />
          <Field label="Duration" value={doc.duration || "—"} />
          <Field
            label="Execution id"
            value={<span className="font-mono text-xs break-all">{doc.execution_id}</span>}
          />
        </dl>
      </div>

      <footer className="flex flex-shrink-0 flex-wrap items-stretch gap-2 border-t border-gray-200 p-4">
        {/* Final Checker is the verification cockpit in BR mode. Shown
            once the BR slot has a settled color — that signals the
            pipeline has finished business-rule extraction and is waiting
            on human verification. Mirrors ocr-ui's BR chip behaviour. */}
        {isFinalCheckerAvailable(doc) ? (
          <Link
            to={buildFinalCheckerHref(doc, hrefOpts)}
            className="inline-flex flex-1 basis-32 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-100"
          >
            <ClipboardCheck className="h-4 w-4 text-amber-700" />
            Final Checker
          </Link>
        ) : null}
        {/* Business Rules is only meaningful once TV has produced
            extracted content, so we hide the CTA until then. */}
        {isTvCompleted(doc) ? (
          <Link
            to={buildBusinessRulesHref(doc, hrefOpts)}
            state={{
              process_id: doc.process_id,
              layout_id: doc.layout_id,
              workflow_id: doc.workflow_id,
            }}
            className="inline-flex flex-1 basis-32 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <ShieldCheck className="h-4 w-4 text-gray-500" />
            Business Rules
          </Link>
        ) : null}
        {/* Tools View needs at least one settled verification signal (TV
            or BC) to be useful — pipeline still mid-extraction means an
            empty cockpit. Hide the button entirely until then so users
            don't see a dimmed CTA they can't act on. */}
        {canOpenVerification(doc) ? (
          <Link
            to={buildVerifyHref(doc, hrefOpts)}
            className="inline-flex flex-1 basis-32 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            Verification
            <ExternalLink className="h-4 w-4" />
          </Link>
        ) : null}
      </footer>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  // `min-w-0` lets the grid column shrink to its allotted width instead
  // of expanding to fit a long unbreakable value (e.g. a layout id with
  // underscores treated as a single token). Without it the layout_id
  // string overflows into the adjacent Service column.
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-gray-900 break-words">{value}</dd>
    </div>
  );
}
